import React, { createContext, useEffect, useMemo, ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { GreenroomState as GS } from '../stores/types/types';
import { ROOM_STATES } from '../stores/reducers/settings';
import { RECORDING_OPTIONS } from '../components/broadcasting/constants';

/**
 * Theme types supported by the application
 */
export type ThemeVersion = 'v2' | 'v3';

/**
 * Helper to get CSS variable value from :root
 */
function getCSSVariable(varName: string): string {
	if (typeof window === 'undefined') return '';
	return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
}

/**
 * Theme-aware color object that reads from CSS variables
 * These values automatically update when theme switches
 */
export interface ThemeColors {
	// Legacy colors
	darkerGray: string;
	darkGray: string;
	gray: string;
	medGray: string;
	white: string;
	purple: string;
	green: string;
	paleGreen: string;
	red: string;
	textGray: string;
	textLight: string;
	popoverPackground: string;
	buttonGray: string;
	nearWhite: string;
	textBlue: string;
	textGreen: string;
	textYellow: string;
	eggplant: string;
	eggplantLight: string;
	initialBackgroundColor: string;
	lemon: string;
	hotpink: string;
	pinkLemonadePink: string;
	black: string;
	gray33: string;

	// Grayscale colors
	blGrayscale100: string;
	blGrayscale200: string;
	blGrayscale300: string;
	blGrayscale400: string;
	blGrayscale500: string;
	blGrayscale600: string;
	blGrayscale700: string;
	blGrayscale800: string;
	blGrayscale900: string;
	blGrayscale1000: string;
	blGrayscale1100: string;
	blGrayscale1200: string;

	// Semantic reds
	blSemanticRed100: string;
	blSemanticRed200: string;
	blSemanticRed300: string;
	blSemanticRed400: string;
	blSemanticRed500: string;
	blSemanticRed600: string;
	blSemanticRed700: string;
	blSemanticRed800: string;
	blSemanticRed900: string;

	blPrimaryRed500: string;
	blPrimaryRed600: string;
}

/**
 * Create theme colors object by reading CSS variables
 */
function createThemeColors(): ThemeColors {
	return {
		// Legacy colors - read from CSS variables
		darkerGray: getCSSVariable('--darker-gray') || '#151516',
		darkGray: getCSSVariable('--primitives-mode-1-gray-1000') || '#1A1A1F',
		gray: 'rgb(28, 28, 30)', // Keeping original as fallback
		medGray: '#54545A', // Keeping original as fallback
		white: getCSSVariable('--primitives-mode-1-gray-100') || '#ffffff',
		purple: getCSSVariable('--purple') || '#362A68',
		green: getCSSVariable('--green') || '#019C00',
		paleGreen: getCSSVariable('--pale-green') || '#A6FF79',
		red: getCSSVariable('--red') || '#FF443A',
		textGray: getCSSVariable('--default-gray') || '#878793',
		textLight: getCSSVariable('--light-gray') || '#B2B2BF',
		popoverPackground: '#151516', // Keeping original
		buttonGray: '#1A1A1F', // Keeping original
		nearWhite: getCSSVariable('--near-white') || '#FCFCFC',
		textBlue: '#0023F5', // Keeping original
		textGreen: '#A2FA4E', // Keeping original
		textYellow: '#FFFC54', // Keeping original
		eggplant: getCSSVariable('--eggplant') || '#522967',
		eggplantLight: getCSSVariable('--eggplant-light') || '#763A95',
		initialBackgroundColor: '#151515', // Keeping original
		lemon: getCSSVariable('--bl-gr-bright-yellow') || '#FFF85C',
		hotpink: getCSSVariable('--bl-hot-pink') || '#EE79B7',
		pinkLemonadePink: getCSSVariable('--pink-lemonade-pink') || '#FF6AE6',
		black: '#000000',
		gray33: '#333333',

		// Grayscale colors - read from CSS variables
		blGrayscale100: getCSSVariable('--primitives-mode-1-gray-100') || '#FFFFFF',
		blGrayscale200: getCSSVariable('--primitives-mode-1-gray-200') || '#E5E4EC',
		blGrayscale300: getCSSVariable('--primitives-mode-1-gray-300') || '#BFBBCF',
		blGrayscale400: getCSSVariable('--primitives-mode-1-gray-400') || '#908CA0',
		blGrayscale500: getCSSVariable('--primitives-mode-1-gray-500') || '#625E70',
		blGrayscale600: getCSSVariable('--primitives-mode-1-gray-600') || '#4F4C57',
		blGrayscale700: getCSSVariable('--primitives-mode-1-gray-700') || '#3C3A42',
		blGrayscale800: getCSSVariable('--primitives-mode-1-gray-800') || '#312F38',
		blGrayscale900: getCSSVariable('--primitives-mode-1-gray-900') || '#26242B',
		blGrayscale1000: getCSSVariable('--primitives-mode-1-gray-1000') || '#1A1A1F',
		blGrayscale1100: getCSSVariable('--primitives-mode-1-gray-1100') || '#121114',
		blGrayscale1200: getCSSVariable('--primitives-mode-1-gray-1200') || '#09090A',

		// Semantic reds - read from CSS variables
		blSemanticRed100: getCSSVariable('--bl-semantic-red-100') || '#FFD7DF',
		blSemanticRed200: getCSSVariable('--bl-semantic-red-200') || '#FFAFBF',
		blSemanticRed300: getCSSVariable('--bl-semantic-red-300') || '#FF879F',
		blSemanticRed400: getCSSVariable('--bl-semantic-red-400') || '#FF5F7F',
		blSemanticRed500: getCSSVariable('--bl-semantic-red-500') || '#FF375F',
		blSemanticRed600: getCSSVariable('--bl-semantic-red-600') || '#CE2E4E',
		blSemanticRed700: getCSSVariable('--bl-semantic-red-700') || '#9D253D',
		blSemanticRed800: getCSSVariable('--bl-semantic-red-800') || '#6B1B2C',
		blSemanticRed900: getCSSVariable('--bl-semantic-red-900') || '#3A121B',

		blPrimaryRed500: getCSSVariable('--bl-primary-red-500') || '#DE352C',
		blPrimaryRed600: getCSSVariable('--bl-primary-red-600') || '#B32C25',
	};
}

/**
 * Theme context value interface
 */
export interface ThemeContextValue {
	/** Current theme version */
	theme: ThemeVersion;
	/** Whether pixel theme (v3) is active */
	isPixelTheme: boolean;
	/** Whether colors-v2 theme is active */
	isV2Theme: boolean;
	/** Theme-aware color constants that read from CSS variables */
	colors: ThemeColors;
}

/**
 * Theme context for accessing current theme state throughout the app
 */
export const ThemeContext = createContext<ThemeContextValue>({
	theme: 'v2',
	isPixelTheme: false,
	isV2Theme: true,
	colors: createThemeColors(),
});

interface ThemeProviderProps {
	children: ReactNode;
}

/**
 * ThemeProvider manages theme switching between colors-v2 and pixel themes.
 *
 * Architecture:
 * - Watches use_title_library feature flag
 * - When FF is ON: Sets data-color-theme="v3", pixel.css primitives override v2
 * - When FF is OFF: Removes attribute, uses hardcoded primitives from _colors-v2.scss
 * - Provides theme state via context for components to consume
 *
 * Selector Strategy:
 * - _colors-v2.scss uses :root:not([data-color-theme]) for conditional application
 * - pixel.css uses :root (always loaded) for primitives
 * - When data-color-theme is present, v2 selector doesn't match, pixel takes over
 */
export function ThemeProvider({ children }: ThemeProviderProps) {
	const doUseWrapper = useSelector((s: GS) => s.SettingsReducer.featureFlags?.use_pixel);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);
	const selectedRecordingOpt = useSelector((s: GS) => s.BroadcastReducer.selectedRecordingOption);

	// Determine current theme based on feature flag
	const theme: ThemeVersion = doUseWrapper ? 'v3' : 'v2';

	// State for colors - updated after theme attribute is set
	const [colors, setColors] = useState<ThemeColors>(() => createThemeColors());


	// Set data-color-theme attribute on html element and update colors
	useEffect(() => {
		const htmlElement = document.documentElement;

		if (doUseWrapper) {
			// Apply pixel theme - pixel.css primitives override v2
			htmlElement.setAttribute('data-color-theme', 'v3');
			htmlElement.setAttribute('data-pixel-theme', 'dark');
			htmlElement.setAttribute('data-pixel-style', 'greenroom-live');
		} else {
			// Remove attribute - v2 colors from _colors-v2.scss apply
			htmlElement.removeAttribute('data-color-theme');
			htmlElement.removeAttribute('data-pixel-theme');
			htmlElement.removeAttribute('data-pixel-style');
		}

		// Re-read colors after a brief delay to allow CSS to update
		// requestAnimationFrame ensures DOM has been updated
		requestAnimationFrame(() => {
			setColors(createThemeColors());
		});

		// Cleanup on unmount
		return () => {
			htmlElement.removeAttribute('data-color-theme');
		};
	}, [doUseWrapper]);

	// confusion on BDEV-4347 and BDEV-2069 are we not recoloring the app
	// useEffect(() => {
	// 	if (roomState === ROOM_STATES.EDITING) {
	// 		document.documentElement.setAttribute('data-pixel-style', 'greenroom-showbuilder');
	// 	} else if (roomState === ROOM_STATES.SHOWTIME) {
	// 		if (selectedRecordingOpt === RECORDING_OPTIONS.RECORD) {
	// 			document.documentElement.setAttribute('data-pixel-style', 'greenroom-recording');
	// 		} else {
	// 			document.documentElement.setAttribute('data-pixel-style', 'greenroom-live');
	// 		}
	// 	}
	// }, [roomState, selectedRecordingOpt])

	const contextValue = useMemo<ThemeContextValue>(() => ({
		theme,
		isPixelTheme: theme === 'v3',
		isV2Theme: theme === 'v2',
		colors,
	}), [theme, colors]);

	return (
		<ThemeContext.Provider value={contextValue}>
			{children}
		</ThemeContext.Provider>
	);
}
